import * as React from "react";
import { Label } from "./label.js";
import { cn } from "../lib/utils.js";

export type FormFieldProps = {
  id?: string;
  label?: React.ReactNode;
  description?: React.ReactNode;
  error?: React.ReactNode;
  required?: boolean;
  optional?: boolean;
  children: React.ReactElement;
  className?: string;
};

let idCounter = 0;
function uid(): string {
  idCounter += 1;
  return `lk-field-${idCounter}`;
}

export function FormField({
  id,
  label,
  description,
  error,
  required,
  optional,
  children,
  className,
}: FormFieldProps): React.ReactElement {
  const reactId = React.useId?.() ?? "";
  const fieldId = id ?? children.props.id ?? `${reactId || uid()}-input`;
  const errorId = `${fieldId}-error`;
  const descriptionId = `${fieldId}-description`;

  const child = React.cloneElement(children, {
    id: fieldId,
    "aria-invalid": error ? true : children.props["aria-invalid"],
    "aria-describedby":
      // The description <p> is not rendered while an error is shown, so
      // only reference it when it actually exists in the DOM.
      [error ? errorId : null, description && !error ? descriptionId : null]
        .filter(Boolean)
        .join(" ") || undefined,
  });

  return (
    <div className={cn("grid gap-1.5", className)}>
      {label && (
        <Label htmlFor={fieldId} className="flex items-center gap-1">
          {label}
          {required && <span className="text-destructive">*</span>}
          {optional && (
            <span className="text-xs font-normal text-muted-foreground">
              (optional)
            </span>
          )}
        </Label>
      )}
      {child}
      {description && !error && (
        <p id={descriptionId} className="text-xs text-muted-foreground">
          {description}
        </p>
      )}
      {error && (
        <p id={errorId} className="text-xs text-destructive">
          {error}
        </p>
      )}
    </div>
  );
}
